// src/hooks/useWebSocket.js — VPS v2.0
// ESP32 connects directly to VPS; browser connects to same VPS /ws
import { useState, useEffect, useRef, useCallback } from 'react';

//const WS_URL = `ws://${window.location.hostname}:${window.location.port}/ws`;
const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const WS_URL = `${wsProtocol}//${window.location.host}/ws`;

export const CONNECTION_STATE = {
  CONNECTING:   'CONNECTING',
  CONNECTED:    'CONNECTED',
  DISCONNECTED: 'DISCONNECTED',
  RECONNECTING: 'RECONNECTING',
};

export function useWebSocket(onMessage) {
  const [state,        setState]        = useState(CONNECTION_STATE.DISCONNECTED);
  const [deviceCount,  setDeviceCount]  = useState(0);
  const [devices,      setDevices]      = useState([]);   // [{ip,rssi,lastSeen,packets}]
  const [totalPackets, setTotalPackets] = useState(0);

  const wsRef            = useRef(null);
  const reconnectTimer   = useRef(null);
  const reconnectAttempts = useRef(0);
  const mountedRef       = useRef(true);

  const connect = useCallback(() => {
    if (!mountedRef.current) return;
    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    setState(CONNECTION_STATE.CONNECTING);

    try {
      const ws = new WebSocket(WS_URL);
      wsRef.current = ws;

      ws.onopen = () => {
        if (!mountedRef.current) return;
        setState(CONNECTION_STATE.CONNECTED);
        reconnectAttempts.current = 0;
      };

      ws.onclose = () => {
        if (!mountedRef.current) return;
        setState(CONNECTION_STATE.RECONNECTING);
        setDeviceCount(0);
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 10000);
        reconnectAttempts.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      };

      ws.onerror = () => setState(CONNECTION_STATE.RECONNECTING);

      ws.onmessage = (evt) => {
        if (!mountedRef.current) return;
        try {
          const msg = JSON.parse(evt.data);

          switch (msg.type) {
            case 'server_hello':
              setDeviceCount(msg.deviceCount || 0);
              setDevices(msg.devices || []);
              setTotalPackets(msg.totalPackets || 0);
              break;

            case 'device_connected':
              setDeviceCount(msg.deviceCount || 0);
              setDevices(d => {
                const exists = d.find(x => x.ip === msg.deviceIp);
                if (exists) return d;
                return [...d, { ip: msg.deviceIp, rssi: 0, lastSeen: Date.now(), packets: 0 }];
              });
              break;

            case 'device_disconnected':
              setDeviceCount(msg.deviceCount || 0);
              setDevices(d => d.filter(x => x.ip !== msg.deviceIp));
              break;

            case 'history':
              // Replay recent history for late-joining browsers
              (msg.packets || []).forEach(p => onMessage && onMessage(p));
              break;

            case 'esp32_status':
              // Update device RSSI
              setDevices(d => d.map(x =>
                x.ip === msg.deviceIp ? { ...x, rssi: msg.rssi ?? x.rssi } : x
              ));
              break;

            case 'data':
              // Update device metadata if included
              if (msg.deviceIp) {
                setDevices(d => d.map(x =>
                  x.ip === msg.deviceIp
                    ? { ...x, rssi: msg.deviceRssi ?? x.rssi, lastSeen: Date.now(), packets: (x.packets || 0) + 1 }
                    : x
                ));
              }
              setTotalPackets(t => t + 1);
              onMessage && onMessage(msg);
              break;

            default:
              break;
          }
        } catch {
          onMessage && onMessage({ type: 'data', raw: evt.data, timestamp: Date.now() });
        }
      };
    } catch (err) {
      console.error('[WS] Failed:', err);
      reconnectTimer.current = setTimeout(connect, 3000);
    }
  }, [onMessage]);

  useEffect(() => {
    mountedRef.current = true;
    connect();
    return () => {
      mountedRef.current = false;
      clearTimeout(reconnectTimer.current);
      wsRef.current?.close();
    };
  }, [connect]);

  const send = useCallback((data) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(typeof data === 'string' ? data : JSON.stringify(data));
    }
  }, []);

  return { state, deviceCount, devices, totalPackets, send };
}
